import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  SqSelect,
  SqSelectContent,
  SqSelectItem,
  SqSelectTrigger,
} from "@/components/squircle-ui/select";
import { getMediaIcon } from "@/screens/utils/Helpers";
import { SqButton } from "@/components/squircle-ui/button";
import { SelectPortal } from "@radix-ui/react-select";

export type CreateEditDialogCreativeType = "video" | "image";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    title: string,
    description: string,
    creativeType?: CreateEditDialogCreativeType,
  ) => void | Promise<void>;
  title: string;
  description: string;
  isEdit?: boolean;
  isDefault?: boolean;
  /** When true, show Media type dropdown and pass selected value as third arg to onSubmit. Used when creating from label dialog. */
  showMediaType?: boolean;
  defaultMediaType?: CreateEditDialogCreativeType;
};

const CreateEditDialog = (props: Props) => {
  const [title, setTitle] = useState(props.title);
  const [description, setDescription] = useState(props.description);
  const [mediaType, setMediaType] = useState<CreateEditDialogCreativeType>(
    props.defaultMediaType ?? "video",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setTitle(props.title);
    setDescription(props.description);
  }, [props.title, props.description]);

  useEffect(() => {
    if (props.showMediaType && props.defaultMediaType) {
      setMediaType(props.defaultMediaType);
    }
  }, [props.showMediaType, props.defaultMediaType]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTitle("");
      setDescription("");
      if (props.showMediaType) setMediaType(props.defaultMediaType ?? "video");
    }
    props.onOpenChange(open);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;
    try {
      setIsSubmitting(true);
      if (props.showMediaType) {
        await Promise.resolve(
          props.onSubmit(title.trim(), description.trim(), mediaType),
        );
      } else {
        await Promise.resolve(props.onSubmit(title.trim(), description.trim()));
      }
      handleOpenChange(false);
    } catch {
      // Leave dialog open; parent shows error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !title.trim() || !description.trim() || isSubmitting;

  return (
    <Dialog open={props.isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[713px] gap-0 p-0 overflow-hidden" hideCloseButton>
        <DialogHeader className="pt-4 px-[18px] space-y-[2px]">
          <DialogTitle className="overflow-hidden text-brand-primary-new text-ellipsis text-[14px] font-medium not-italic leading-[18px]">
            {props.isEdit ? "Edit Overview" : "Create Overview"}
          </DialogTitle>
          <p className="text-brand-primary-600 text-[14px] leading-[18px]">
            Provide a title and overview description so we can retrieve the right details
            from images or videos.
          </p>
        </DialogHeader>

        <div className="pr-[14px] pl-[13px] pt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Input
              id="title"
              placeholder="Enter overview title"
              disabled={props.isDefault}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-[32px] w-full px-[14px] py-[6px] text-[12px] font-medium placeholder:font-normal placeholder:text-brand-primary-400 text-brand-primary-800 rounded-[8px] border-[1px] border-[#D0D5DD99] bg-[#FBFBFB] shadow-none"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Textarea
              id="description"
              placeholder="Describe what to extract from each creative. We'll pull that info from every creative so labels have more context and can form better groups."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[113px] px-[14px] pt-2 pb-[6px] resize-none placeholder:text-brand-primary-400 focus-visible:ring-0 text-brand-primary-800 text-[12px] font-normal rounded-[8px] border-[1px] border-[#D0D5DD99] bg-[#FBFBFB] shadow-none w-full"
              rows={4}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pr-[14px] pl-[13px] py-4 mt-[13px] border-t border-[#D0D5DD99]">
          <div className="flex items-center gap-2">
            {props.showMediaType && (
              <>
                <span className="text-[12px] font-medium text-brand-primary-800">Media type:</span>
                <SqSelect value={mediaType} onValueChange={(v) => setMediaType(v as CreateEditDialogCreativeType)}>
                  <SqSelectTrigger className="h-[32px] p-[6px_12px] flex justify-center items-center gap-[6px] rounded-[8px] text-[12px] font-normal leading-[19.157px] tracking-[0.24px] text-brand-primary-800">
                    {getMediaIcon(mediaType, true, 12, 18)}
                    {mediaType === "video" ? "Videos" : "Images"}
                  </SqSelectTrigger>
                  <SelectPortal>

                  <SqSelectContent className="p-0">
                    <SqSelectItem value="video" className="flex items-center gap-[6px] text-[12px] font-normal leading-[19.157px] tracking-[0.24px] text-brand-primary-800">
                      {getMediaIcon("video", true, 12, 18)}
                      Videos
                    </SqSelectItem>
                    <SqSelectItem value="image" className="flex items-center gap-[6px] text-[12px] font-normal leading-[19.157px] tracking-[0.24px] text-brand-primary-800">
                      {getMediaIcon("image", true, 12, 18)}
                      Images
                    </SqSelectItem>
                  </SqSelectContent>
                  </SelectPortal>
                </SqSelect>
              </>
            )}
          </div>
          <div className="flex gap-3">

            <SqButton
              variant="default"
              size="md"
              onClick={() => void handleSubmit()}
              disabled={isSubmitDisabled}
              className="font-medium"
            >
              {props.isEdit ? "Save" : isSubmitting ? "Creating…" : "Create"}
            </SqButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEditDialog;
